import { useRef, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getPostLoginPath } from "../app/getPostLoginPath";
import authRepository from "../repositories/authRepository";
import { logout, login } from "../store/authSlice";
import { UserRepository } from "../repositories/userRepository";
import { clearUser, setUser } from "../store/userSlice";
import { RecaptchaVerifier, ConfirmationResult, User } from "firebase/auth";
import { showError } from "../library/feedback";



export const useLoginHook = ({ routeBackTo, shouldLogout }: { routeBackTo?: string, shouldLogout?: boolean }) => {
    const inputEmail = useRef("");
    const inputPassword = useRef("");
    const [otpSent, setOtpSent] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
    const recaptchaVerifier = useRef<RecaptchaVerifier | null>(null);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        shouldLogout && authRepository.logout().then(() => {
            dispatch(logout());
        })
    }, [shouldLogout, dispatch]);

    const handlePostLogin = async (user: User) => {
        const userData = await UserRepository.getUserDetails(user.uid);
        dispatch(setUser(userData?.toJson()));
        dispatch(login(authRepository.parseUser(user)));
        const fallbackPath = routeBackTo || "/BuyJewelleries";
        navigate(userData?.isAdmin ? getPostLoginPath(userData.toJson()) : fallbackPath);
    }

    const handleLogin = () => {
        authRepository.simpleLogin(inputEmail.current, inputPassword.current).then(async user => {
            await handlePostLogin(user);
        })
    }

    const handleGoogleLogin = async () => {
        const user = await authRepository.googleOAuthLogin();
        if (user) await handlePostLogin(user);
    }

    const handleSendOtp = async (phoneNumber: string) => {
        if (isSending) return;
        setIsSending(true);

        let formattedNumber = phoneNumber.trim();
        if (!formattedNumber.startsWith('+')) {
            if (formattedNumber.length === 10) {
                formattedNumber = `+91${formattedNumber}`;
            } else {
                console.warn("Phone number might be missing country code");
            }
        }

        const container = document.getElementById('recaptcha-container');
        if (!container) {
            console.error("Recaptcha container not found in DOM");
            setIsSending(false);
            return;
        }

        // Always clear and recreate the verifier to ensure a fresh token and valid element
        if (recaptchaVerifier.current) {
            try {
                recaptchaVerifier.current.clear();
            } catch (e) {
                console.warn("Error clearing recaptcha:", e);
            }
        }

        recaptchaVerifier.current = new RecaptchaVerifier(authRepository.getAuth(), container, {
            'size': 'invisible',
        });

        try {
            const confirmation = await authRepository.loginWithPhone(formattedNumber, recaptchaVerifier.current);
            setConfirmationResult(confirmation);
            setOtpSent(true);
            return confirmation;
        } catch (error: any) {
            console.error("Error sending OTP:", error);
            if (error.code === 'auth/too-many-requests') {
                showError("Too many requests! Please try again later.");
            } else {
                showError("Error sending OTP please try again later.");
            }
            // Reset the recaptcha widget on error so the user can try again
            if (recaptchaVerifier.current) {
                const widgetId = await recaptchaVerifier.current.render();
                const { grecaptcha } = window as Window & { grecaptcha?: { reset: (widgetId: number) => void } };
                grecaptcha?.reset(widgetId);
            }
            throw error;
        } finally {
            setIsSending(false);
        }
    }

    const handleVerifyOtp = async (otp: string) => {
        if (!confirmationResult) {
            throw new Error("No confirmation result found");
        }
        try {
            const result = await confirmationResult.confirm(otp);
            const user = result.user;
            await handlePostLogin(user);
            return user;
        } catch (error) {
            console.error("Error verifying OTP:", error);
            throw error;
        }
    }

    async function handleLogout() {
        await authRepository.logout();
        dispatch(clearUser());
        dispatch(logout());
    }

    return {
        inputEmail,
        inputPassword,
        handleLogin,
        handleGoogleLogin,
        handleLogout,
        handleSendOtp,
        handleVerifyOtp,
        otpSent,
        isSending
    }
}